import { css } from '@emotion/react';
import { BirdiTheme, buttonBlue, buttonOutlineBlue } from '@birdi/theme';

/**
 * Green primary pill — matches the dashboard's green "Create" button
 * (common/map-dashboard-header/create-dropdown.tsx `triggerStyles`): compact
 * 8px/16px padding, brand green (theme.flora === #4AD294), 40px radius.
 */
export const buttonGreen = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${theme.mono0};
  background: ${theme.flora};
  border: none;
  border-radius: 40px;
  white-space: nowrap;
  cursor: pointer;
  &:hover:enabled {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  &:focus {
    outline: none;
  }
`;

// Shared modal chrome so every Map Sites dialog (create / rename / update
// address / delete) has an identical separator, footer and button sizing.
export const modalSeparatorCSS = (theme: BirdiTheme) => css`
  border: none;
  border-top: 1px solid ${theme.mono20};
  margin: 0.75rem 0 1.5rem;
`;

export const modalFooterCSS = css`
  display: flex;
  gap: 1rem;
  padding-top: 1.5rem;
`;

export const modalHalfButtonCSS = css`
  flex: 1;
  border-radius: 8px;
  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

// Confirm-dialog footer: the two buttons sit together in the right half of the
// modal, splitting it evenly (a quarter of the modal each).
export const confirmFooterCSS = css`
  display: flex;
  gap: 1rem;
  width: 50%;
  margin-left: auto;
  padding-top: 1.5rem;
`;

export const cancelButtonCSS = (theme: BirdiTheme) => css`
  ${buttonOutlineBlue(theme)};
  ${modalHalfButtonCSS};
`;

export const primaryButtonCSS = (theme: BirdiTheme) => css`
  ${buttonBlue(theme)};
  ${modalHalfButtonCSS};
`;

// Shared modal text input — deepOcean focus ring (not the browser blue).
export const modalInputCSS = (theme: BirdiTheme) => css`
  width: 100%;
  box-sizing: border-box;
  padding: 0.6rem 0.8rem;
  font-size: 1rem;
  border: 1px solid ${theme.mono40};
  border-radius: 2px;
  &:focus {
    outline: none;
    border-color: ${theme.deepOcean};
    box-shadow: 0 0 0 1px ${theme.deepOcean};
  }
`;

// react-select `theme` override so the focused control border + selected/hover
// option colours use Birdi's deepOcean instead of react-select's default blue.
export const selectTheme = (birdi: BirdiTheme) => (base: any) => ({
  ...base,
  colors: {
    ...base.colors,
    primary: birdi.deepOcean,
    primary25: birdi.lightOcean,
    primary50: birdi.lightOcean,
  },
});

export const cardCSS = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: column;
  background: ${theme.mono0};
  border: solid 1px ${theme.mono30};
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 2px 6px rgba(9, 30, 66, 0.08);
  }
`;

export const coverCSS = (theme: BirdiTheme) => css`
  position: relative;
  width: 100%;
  height: 9rem;
  background: ${theme.mono20};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const metaRowCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: ${theme.mono50};
  font-size: 0.875rem;
  svg {
    width: 1em;
    height: 1em;
    flex-shrink: 0;
  }
`;

export const listRowCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: ${theme.mono0};
  border: solid 1px ${theme.mono30};
  border-radius: 10px;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 2px 6px rgba(9, 30, 66, 0.08);
  }
`;

export const emptyStateCSS = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  padding: 0.5rem 0;
  text-align: left;
  h3 {
    margin: 0;
    color: ${theme.midnight};
  }
  p {
    margin: 0;
    color: ${theme.mono50};
  }
`;

export const infoBannerCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  background: ${theme.lightOcean};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  font-size: 0.9rem;
  color: ${theme.midnight};
  svg {
    flex-shrink: 0;
    margin-top: 0.15em;
  }
`;

export const tabButtonCSS = (theme: BirdiTheme, active: boolean) => css`
  display: flex;
  align-items: center;
  gap: 0.85em;
  padding: 1em 2.75em;
  background: transparent;
  color: ${active ? theme.deepOcean : theme.midnight};
  font-weight: ${active ? 'bold' : 'normal'};
  border: none;
  border-bottom: 3px solid ${active ? theme.deepOcean : 'transparent'};
  cursor: pointer;
  &:hover {
    color: ${theme.deepOcean};
  }
`;

export const countPillCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5em;
  padding: 0 0.4em;
  border-radius: 1em;
  background: ${theme.lightOcean};
  color: ${theme.deepOcean};
  font-size: 0.8em;
  font-weight: bold;
`;
